import {
    deleteNotificationRule,
    deleteNotificationRuleForUser,
    deleteRecipient,
    generateNotificationRule,
    getNotificationRecipients,
    getNotificationRuleTemplates,
    getUserNotificationRules,
    updateNotificationRule,
    upsertRecipient,
} from '@/api/notification.api';
import type { MaybeRef } from '@/shared/typings';
import { type INotificationRule, NotificationTemplateMap } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, unref } from 'vue';

export const useNotificationTemplatesWithRules = (userId?: MaybeRef<string>, userEmail?: MaybeRef<string>) => {
    const queryClient = useQueryClient();

    // Combined query that fetches both templates and rules
    const query = useQuery(
        ['user/notification-templates-with-rules', userId],
        async () => {
            const [templates, rules] = await Promise.all([getNotificationRuleTemplates(), userId ? getUserNotificationRules(unref(userId)) : []]);

            // Enrich templates with their related rules
            return templates
                .map(template => ({
                    ...template,
                    title: NotificationTemplateMap[template.name]?.title || template.name,
                    description: NotificationTemplateMap[template.name]?.description || template.name,
                    rules: rules
                        .filter(rule => rule.notificationRuleTemplateId === template.notificationRuleTemplateId)
                        .sort((a, b) => Number(b.active) - Number(a.active)),
                    ruleCount: rules.filter(rule => rule.notificationRuleTemplateId === template.notificationRuleTemplateId).length,
                    activeRuleCount: rules.filter(rule => rule.notificationRuleTemplateId === template.notificationRuleTemplateId && rule.active).length,
                }))
                .sort((a, b) => a.title.localeCompare(b.title));
        },
        {
            enabled: computed(() => !!userId),
            initialData: [],
        },
    );

    const { mutateAsync: deleteRuleMutation } = useMutation(async (notificationRuleId: number) => deleteNotificationRule(notificationRuleId), {
        onSuccess: () => {
            // Just invalidate and refetch - simpler and more reliable
            queryClient.invalidateQueries(['user/notification-templates-with-rules', userId]);
        },
        onError: error => {
            console.error('Failed to delete rule:', error);
        },
    });

    const { mutateAsync: deleteRulesForUserMutation } = useMutation(async (userUid: string) => deleteNotificationRuleForUser(userUid), {
        onSuccess: () => {
            // Just invalidate and refetch - simpler and more reliable
            queryClient.invalidateQueries(['user/notification-templates-with-rules', userId]);
        },
        onError: error => {
            console.error('Failed to delete rule:', error);
        },
    });

    // Optimistic update mutation for rules
    const { mutateAsync: updateRuleMutation } = useMutation(async (rule: INotificationRule) => updateNotificationRule(rule), {
        onSuccess: () => {
            // Just invalidate and refetch - simpler and more reliable
            queryClient.invalidateQueries(['user/notification-templates-with-rules', userId]);
        },
        onError: error => {
            console.error('Failed to update rule:', error);
        },
    });

    const { mutateAsync: generateNotificationRuleMutation } = useMutation(
        async (params: { rules: { templateName: string; tokens: Record<string, any> }[]; userUid?: MaybeRef<string>; userEmail?: MaybeRef<string> }) => {
            const userIdResolved = unref(params.userUid || userId);
            const userEmailResolved = unref(params.userEmail || userEmail);
            return await generateNotificationRule(params.rules, userIdResolved ? { uid: userIdResolved, email: userEmailResolved } : undefined);
        },
        {
            onSuccess: () => {
                // Just invalidate and refetch - simpler and more reliable
                queryClient.invalidateQueries(['user/notification-templates-with-rules', userId]);
            },
            onError: error => {
                console.error('Failed to generate rule:', error);
            },
        },
    );

    return {
        ...query,
        deleteNotificationRule: deleteRuleMutation,
        deleteNotificationRulesForUser: deleteRulesForUserMutation,
        updateNotificationRule: updateRuleMutation,
        generateNotificationRule: generateNotificationRuleMutation,
    };
};
export const useNotificationRecipientsByProvider = (provider: RecipientProvider) => {
    return useQuery(['user/notification-recipients', provider], async () => getNotificationRecipients(provider), {
        initialData: [],
    });
};

export const useUpsertRecipient = () => {
    const queryClient = useQueryClient();
    return useMutation(async (recipient: MaybeRef<IBaseRecipient>) => upsertRecipient(unref(recipient)), {
        onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: ['user/notification-recipients', data.provider] });
        },
    });
};

export const useDeleteRecipient = () => {
    const queryClient = useQueryClient();
    return useMutation(async (recipient: MaybeRef<IBaseRecipient>) => deleteRecipient(unref(recipient)), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user/notification-recipients'] });
        },
    });
};

export const useGenerateNotificationRule = (userId?: MaybeRef<string>, userEmail?: MaybeRef<string>) => {
    const queryClient = useQueryClient();
    return useMutation(
        async (params: { rules: { templateName: string; tokens: Record<string, any> }[]; userUid?: MaybeRef<string>; userEmail?: MaybeRef<string> }) => {
            const userIdResolved = unref(params.userUid || userId);
            const userEmailResolved = unref(params.userEmail || userEmail);
            return generateNotificationRule(params.rules, userIdResolved ? { uid: userIdResolved, email: userEmailResolved } : undefined);
        },
        {
            onSuccess: generatedRules => {
                queryClient.setQueryData(['user/notification-rules', unref(userId)], (oldRules: INotificationRule[]) =>
                    uniqBy([...generatedRules, ...(oldRules ?? [])], 'notificationRuleId'),
                );
                queryClient.invalidateQueries({ queryKey: ['user/notification-recipients'] });
            },
        },
    );
};
